import { useCallback, useState } from 'react';

import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import {
  IKnmErknmFormViolation,
  IKnmErknmFormViolationFix,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ErknmDecisionViolationFixFactTypeField } from 'DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixFactTypeField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ErknmDecisionViolationFixAttachmentsField } from 'DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixAttachmentsField';
import { ViolationFixGradesEnum } from 'APIPackage/KnmModule/enums/ViolationFixGradesEnum';
import { ViolationFixMethodsEnum } from 'APIPackage/KnmModule/enums/ViolationFixMethodsEnum';
import { ViolationFixMaterialTypesEnum } from 'APIPackage/KnmModule/enums/ViolationFixMaterialTypesEnum';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IViolationFixGradeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IViolationFixGradeSelectOption';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { violationSyncRegistry } from 'KnmErknmPackage/ErknmViolationsModule/helpers/violationSyncRegistry';
import { IViolationFixMethodSelectOption } from 'CorePackage/CatalogsModule/interfaces/IViolationFixMethodSelectOption';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import styles from '../../../KnmErknmPackage/ErknmViolationsModule/components/ErknmViolationItem.module.scss';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { ReactQuillEditorField } from '../../../UIPackage/MainModule/components/ReactQuillEditorField';

interface IErknmDecisionViolationFixItemProps {
  violation: IKnmErknmFormViolation;
  fix: IKnmErknmFormViolationFix;
  fixIdx: number;
  fieldName: string;
  name: string;
  isFixedFieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  watchOnly: boolean;
  isPaperless: boolean;
  inAct?: boolean;
  onRemove: () => void;
  isDisabledByDirty?: boolean;
  isDisabledByAgreement?: boolean;
  hasUnsignedAct?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  id: string;
  inAgreement?: boolean;
  watchOnlyRange?: boolean;
  isExpandAll?: boolean;
}

function getFixTitleNote(fix: IKnmErknmFormViolationFix): string {
  if (fix.isNew) {
    return '';
  }

  return fix.method?.value === ViolationFixMethodsEnum.MOBILE_APP_INFO
    ? ' (представлен контролируемым лицом)'
    : ' (представлен инспектором)';
}

export const ErknmDecisionViolationFixItem = ({
  fieldName,
  fixIdx,
  formRef,
  violation,
  fix,
  watchOnly,
  onRemove,
  isPaperless,
  inAct,
  isDisabledByDirty = false,
  isDisabledByAgreement = false,
  hasUnsignedAct = false,
  isKnm,
  isPm,
  isDecision,
  id,
  inAgreement,
  watchOnlyRange,
  isExpandAll,
  name,
  isFixedFieldName,
}: IErknmDecisionViolationFixItemProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);

  const isFixedName = name && isFixedFieldName ? `${name}.${isFixedFieldName}` : undefined;

  const hasMaterialWithExplanation: boolean = !!fix.type?.some(
    typeItem => typeItem.value?.value === ViolationFixMaterialTypesEnum.EXPLANATION,
  );
  const hasMaterialWithFiles: boolean = !!fix.type?.some(
    typeItem =>
      typeItem.value?.value === ViolationFixMaterialTypesEnum.DOCUMENT ||
      typeItem.value?.value === ViolationFixMaterialTypesEnum.PHOTO ||
      typeItem.value?.value === ViolationFixMaterialTypesEnum.VIDEO,
  );
  const hasNotFixedGrade: boolean = fix.grade?.value === ViolationFixGradesEnum.NOT_CORRECT;
  const isViolationOnApprove = violation.status === ViolationStatusesEnum.APPROVING;
  // @ts-ignore
  const fixGradeInitialValue = formRef?.current.getFieldState(`${fieldName}.grade` as keyof IKnmForm)?.initial;
  // @ts-ignore
  const fixGradeInfoInitialValue = formRef?.current.getFieldState(`${fieldName}.explanationGrade` as keyof IKnmForm)
    ?.initial;
  // @ts-ignore
  const fixFactMethodInitialValue = formRef?.current.getFieldState(`${fieldName}.method` as keyof IKnmForm)?.initial;

  if (!hasMaterialWithExplanation && (fix.explanationMaterial || fix.explanationMaterialReactQuillEditor)) {
    // @ts-ignore
    formRef?.current.change(`${fieldName}.explanationMaterial` as keyof IKnmForm);
    // @ts-ignore
    formRef?.current.change(`${fieldName}.explanationMaterialReactQuillEditor` as keyof IKnmForm);
  }
  if (!hasMaterialWithFiles && fix.attachments?.length) {
    // @ts-ignore
    formRef?.current.change(`${fieldName}.attachments` as keyof IKnmForm);
  }
  if (!hasNotFixedGrade && fix.explanationGrade) {
    // @ts-ignore
    formRef?.current.change(`${fieldName}.explanationGrade` as keyof IKnmForm);
  }

  // Устранено ли в ходе другого мероприятия
  const isFixedDuringAnotherEvent = fixFactMethodInitialValue?.id === 5;

  const getFilterAixFactMethodSelectOptions = useCallback(
    (arr: ISelectOption<string | number>[] | undefined) => {
      return arr
        ?.map(item => ({
          ...item,
          isDisabled: item.id === 1 || item.id === 5,
        }))
        .filter(e => !isFixedDuringAnotherEvent && e.id !== 5);
    },
    [isFixedDuringAnotherEvent],
  );

  const hasError = checkHasErrorInCollapseBlock(formRef, fieldName);

  const handleOnChangeGrade = useCallback(
    (gradeSelectOption: IViolationFixGradeSelectOption) => {
      if (gradeSelectOption.value === ViolationFixGradesEnum.CORRECT && isFixedName) {
        const positiveBooleanOption = booleanSelectOptions.find(b => b.value);

        // @ts-ignore
        formRef?.current.change(isFixedName as keyof IKnmForm, positiveBooleanOption);
      } else if (gradeSelectOption.value === ViolationFixGradesEnum.NOT_CORRECT && isFixedName) {
        const negativeBooleanOption = booleanSelectOptions.find(b => !b.value);

        // @ts-ignore
        formRef?.current.change(isFixedName as keyof IKnmForm, negativeBooleanOption);
      }
    },
    [formRef, isFixedName],
  );

  const handleOnChangeMethod = useCallback(
    (methodSelectOption: IViolationFixMethodSelectOption) => {
      violationSyncRegistry.syncField(violation.id || '0', fieldName, 'fixFactMethod', methodSelectOption);
    },
    [formRef, fieldName],
  );

  return (
    <KnmCollapse
      id={id}
      title={`Материал №${fixIdx + 1}${getFixTitleNote(fix)}`}
      bodyClassName={styles.ItemCollapseBody}
      // Можно редактировать нарушения если еще акт не был подписан
      onClose={(fix.isNew || hasUnsignedAct) && !isDisabledByDirty && !isDisabledByAgreement ? onRemove : undefined}
      openByDefault={fix.isNew || isExpandAll}
      headerPostfix={isPaperless && fix.isNew && <div className={styles.ActStatus}>Черновик</div>}
      changeOpenedCallback={setIsKnmCollapseOpened}
      error={isKnmCollapseOpened && hasError}
    >
      <>
        <Row margin="none">
          <ErknmDecisionViolationFixFactTypeField
            fix={fix}
            violation={violation}
            fieldName={fieldName}
            formRef={formRef}
            inAct={inAct}
            isDisabledByDirty={isDisabledByDirty}
            isDisabledByAgreement={isDisabledByAgreement}
            hasUnsignedAct={hasUnsignedAct}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
            id={id}
          />
        </Row>
        {hasMaterialWithExplanation && (
          <Row>
            <FieldBlock
              id={`${id}.explanationMaterialReactQuillEditor`}
              label={getTitleFieldWithRedStar({
                label: 'Пояснение',
                field: `${fieldName}.explanationMaterialReactQuillEditor`,
                isPm,
                isKnm,
                isDecision,
              })}
              placementLabel="left"
              labelFontSize="small"
              labelSize="col-3"
              color="black"
            >
              <ReactQuillEditorField
                className="shared-row-field"
                id={`${fieldName}.explanationMaterialReactQuillEditor`}
                name={`${fieldName}.explanationMaterialReactQuillEditor`}
                placeholder="Введите пояснение"
                disabled={!(fix.isNew || hasUnsignedAct) || isDisabledByDirty || isDisabledByAgreement}
                handleCustomChange={(e: string) => {
                  // @ts-ignore
                  formRef?.current?.change(`${fieldName}.explanationMaterial` as string, e);
                }}
              />
            </FieldBlock>
          </Row>
        )}
        <Row>
          <FieldBlock
            id={`${id}.method`}
            label={getTitleFieldWithRedStar({
              label: 'Фактический способ устранения нарушения',
              field: `${fieldName}.method`,
              isPm,
              isKnm,
              isDecision,
            })}
            placementLabel="left"
            labelFontSize="small"
            labelSize="col-3"
            color="black"
          >
            <SelectInputFieldAutoOptions
              className="shared-row-field"
              id={`${fieldName}.method`}
              name={`${fieldName}.method`}
              optionName={CommonCatalogsNamesEnum.VIOLATION_FIX_METHOD}
              transformFunc={getFilterAixFactMethodSelectOptions}
              isClearable={false}
              disabled={!(fix.isNew || hasUnsignedAct) || isDisabledByDirty || isDisabledByAgreement}
              onChange={handleOnChangeMethod}
            />
          </FieldBlock>
        </Row>
        {hasMaterialWithFiles && (
          <ErknmDecisionViolationFixAttachmentsField
            formRef={formRef}
            fieldName={fieldName}
            fix={fix}
            watchOnly={watchOnly}
            isDisabledByDirty={isDisabledByDirty}
            isDisabledByAgreement={isDisabledByAgreement}
            hasUnsignedAct={hasUnsignedAct}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
            id={id}
          />
        )}
        {fix.method?.value === ViolationFixMethodsEnum.MOBILE_APP_INFO ? (
          <Row>
            <FieldBlock
              label={
                <Row margin="none" notSpace>
                  <span>Оценка исправления нарушения</span>
                </Row>
              }
              placementLabel="left"
              labelFontSize="small"
              labelSize="col-3"
              color="black"
            >
              <SelectInputFieldAutoOptions
                className="shared-row-field"
                id={`${fieldName}.grade`}
                name={`${fieldName}.grade`}
                optionName={CommonCatalogsNamesEnum.VIOLATION_FIX_GRADE}
                disabled={
                  !(isViolationOnApprove || fix.isNew || hasUnsignedAct) ||
                  !!fixGradeInitialValue ||
                  isDisabledByDirty ||
                  isDisabledByAgreement ||
                  (inAgreement && watchOnlyRange)
                }
                isClearable={false}
                onChange={handleOnChangeGrade}
              />
            </FieldBlock>
          </Row>
        ) : null}
        {hasNotFixedGrade && (
          <Row>
            <FieldBlock label="Пояснение" placementLabel="left" labelFontSize="small" labelSize="col-3" color="black">
              <TextareaField
                className="shared-row-field"
                name={`${fieldName}.explanationGrade`}
                placeholder="Введите пояснение (при необходимости)"
                disabled={
                  !(isViolationOnApprove || fix.isNew || hasUnsignedAct) ||
                  !!fixGradeInfoInitialValue ||
                  isDisabledByDirty ||
                  isDisabledByAgreement ||
                  (inAgreement && watchOnlyRange)
                }
                autoHeight
              />
            </FieldBlock>
          </Row>
        )}
      </>
    </KnmCollapse>
  );
};
